/*
 * media-gallery.js  —  Dawn media gallery + MOBILE-ONLY Swiper slider
 * -------------------------------------------------------------------
 * Desktop (>= 750px) behaves exactly like stock Dawn.
 * Mobile (< 750px) uses Swiper instead of the native scroll-snap slider.
 *
 * Everything else is preserved:
 *  - tap to open zoom / lightbox modal (product-modal.js)
 *  - deferred video + external video (autoplay on poster tap)
 *  - 3D models / shopify-xr
 *  - thumbnail <-> slide two-way sync
 *  - variant change (setActiveMedia + prepend)
 *  - the existing Dawn prev/next arrows and "1 / 5" counter
 */

/* ------------------------------------------------------------------ */
/* Config — tweak these, nothing else                                  */
/* ------------------------------------------------------------------ */
const MOBILE_GALLERY_SWIPER = {
  // Anything BELOW this is "mobile". Must match Dawn's breakpoint.
  desktopMediaQuery: '(min-width: 750px)',

  // Slide width as a fraction of the gallery width.
  // 1 = one full slide. 0.88 = next image peeks in from the right.
  slideWidthRatio: 1,

  // Space each side of the image, in px. This is padding INSIDE the slide,
  // so slide width still equals the snap step and nothing can drift.
  // Dawn's mobile page gutter is 1.5rem = 15px. Use 0 for edge-to-edge.
  slideGutter: 0,

  // Extra gap BETWEEN slides. Usually 0 — slideGutter already separates them.
  spaceBetween: 0,

  // Show Swiper dots under the gallery (Dawn's numeric counter stays either way).
  pagination: true,

  // true = container height animates to each slide. false = matches Dawn today.
  autoHeight: false,

  // Infinite loop. Leave false — loop clones duplicate data-media-id and
  // that breaks the thumbnail / modal / variant lookups.
  loop: false,

  // Start playing a video as soon as you swipe onto its slide.
  // false = Dawn's current behaviour (tap the poster to play).
  playMediaOnSlideChange: false,

  // Swap these two for local assets if you'd rather not use a CDN:
  //   js:  window.__swiperJsUrl   css: window.__swiperCssUrl  (set in Liquid)
  js: 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js',
  css: 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css',
};

/* ------------------------------------------------------------------ */
/* Lazy loader — Swiper is only fetched on mobile, once per page       */
/* ------------------------------------------------------------------ */
let swiperLibraryPromise = null;

function loadSwiperLibrary() {
  if (window.Swiper) return Promise.resolve(window.Swiper);
  if (swiperLibraryPromise) return swiperLibraryPromise;

  const cssHref = window.__swiperCssUrl || MOBILE_GALLERY_SWIPER.css;
  const jsSrc = window.__swiperJsUrl || MOBILE_GALLERY_SWIPER.js;

  swiperLibraryPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-swiper-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssHref;
      link.setAttribute('data-swiper-css', '');
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = jsSrc;
    script.async = true;
    script.onload = () => resolve(window.Swiper);
    script.onerror = () => reject(new Error('Swiper failed to load'));
    document.head.appendChild(script);
  });

  return swiperLibraryPromise;
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia(MOBILE_GALLERY_SWIPER.desktopMediaQuery);

        /* --- mobile swiper bootstrap (runs before the thumbnails guard) --- */
        // this.mobileSwiper = null;
        // this.onBreakpointChange = this.onBreakpointChange.bind(this);
        // this.onNavButtonClick = this.onNavButtonClick.bind(this);
        // this.mql.addEventListener('change', this.onBreakpointChange);
        // this.initMobileSwiper();
        this.mobileSwiper = null;

        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onNavButtonClick = this.onNavButtonClick.bind(this);
        this.onModalClosed = this.onModalClosed.bind(this);

        this.mql.addEventListener('change', this.onBreakpointChange);
        document.body.addEventListener('modalClosed', this.onModalClosed);

        this.initMobileSwiper();
        /* ------------------------------------------------------------------ */

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // disconnectedCallback() {
      //   this.mql.removeEventListener('change', this.onBreakpointChange);
      //   this.destroyMobileSwiper();
      // }
      disconnectedCallback() {
        this.mql.removeEventListener('change', this.onBreakpointChange);
        document.body.removeEventListener('modalClosed', this.onModalClosed);
        this.destroyMobileSwiper();
      }

      /* ================================================================
       * MOBILE SWIPER
       * ================================================================ */

      get mobileSwiperEnabled() {
        return this.dataset.mobileSwiper !== 'false';
      }

      onBreakpointChange() {
        if (this.mql.matches) {
          this.destroyMobileSwiper();
        } else {
          this.initMobileSwiper();
        }
      }

      onModalClosed() {
        if (!this.mobileSwiper || this.mql.matches) return;

        requestAnimationFrame(() => {
          if (!this.mobileSwiper || this.mobileSwiper.destroyed) return;

          const activeElement = document.activeElement;

          // Dawn returns focus to the image modal opener.
          // Move focus away from it after the modal has completely closed.
          if (
            activeElement &&
            activeElement.closest &&
            activeElement.closest('.product__modal-opener')
          ) {
            activeElement.blur();
          }
        });
      }

      initMobileSwiper() {
        if (!this.mobileSwiperEnabled) return;
        if (this.mql.matches) return; // desktop — stock Dawn
        if (this.mobileSwiper || this.swiperBooting) return;
        if (!this.elements.viewer) return;

        const list = this.elements.viewer.querySelector('ul.product__media-list');
        if (!list) return;
        if (list.querySelectorAll('.product__media-item').length < 2) return;

        this.swiperBooting = true;

        loadSwiperLibrary()
          .then((Swiper) => {
            this.swiperBooting = false;
            if (!Swiper || this.mql.matches || this.mobileSwiper) return;
            this.buildMobileSwiper(Swiper, list);
          })
          .catch(() => {
            // Swiper unavailable -> Dawn's native slider keeps working. No breakage.
            this.swiperBooting = false;
          });
      }

      /*
       * Writes the slide width as an inline !important style. Inline
       * !important beats every stylesheet, so Dawn's .grid--peek width and
       * max-width rules can't shrink a slide below the translate step —
       * which is what causes each slide to sit further left than the last.
       * Runs even if component-mobile-swiper-gallery.css never loaded.
       */
      syncSlideWidths(container) {
        const el = container || (this.mobileSwiper && this.mobileSwiper.el);
        if (!el) return 0;

        const galleryWidth = el.clientWidth;
        if (!galleryWidth) return 0;

        const ratio = parseFloat(this.dataset.slideWidthRatio) || MOBILE_GALLERY_SWIPER.slideWidthRatio;
        const slideWidth = Math.round(galleryWidth * ratio);

        const gutterAttr = parseInt(this.dataset.slideGutter, 10);
        const gutter = Number.isNaN(gutterAttr) ? MOBILE_GALLERY_SWIPER.slideGutter : gutterAttr;

        el.querySelectorAll('.product__media-item').forEach((slide) => {
          // border-box means the gutter eats into the slide instead of adding
          // to it, so slide width stays exactly equal to the translate step.
          slide.style.setProperty('box-sizing', 'border-box', 'important');
          slide.style.setProperty('width', `${slideWidth}px`, 'important');
          slide.style.setProperty('max-width', 'none', 'important');
          slide.style.setProperty('min-width', '0', 'important');
          slide.style.setProperty('flex-shrink', '0', 'important');
          slide.style.setProperty('margin-left', '0', 'important');
          slide.style.setProperty('margin-right', '0', 'important');
          slide.style.setProperty('padding-left', `${gutter}px`, 'important');
          slide.style.setProperty('padding-right', `${gutter}px`, 'important');
        });

        return slideWidth;
      }

      buildMobileSwiper(Swiper, list) {
        // 1. Wrap the existing <ul> in a .swiper container, in place.
        const container = document.createElement('div');
        container.className = 'product-media-swiper swiper';
        list.parentNode.insertBefore(container, list);
        container.appendChild(list);

        // 2. Give Dawn's markup the classes Swiper expects.
        list.classList.add('swiper-wrapper');
        const slides = list.querySelectorAll('.product__media-item');
        slides.forEach((slide) => slide.classList.add('swiper-slide'));

        // 3. Optional dots.
        let paginationEl = null;
        if (MOBILE_GALLERY_SWIPER.pagination) {
          paginationEl = document.createElement('div');
          paginationEl.className = 'product-media-swiper__pagination swiper-pagination';
          container.appendChild(paginationEl);
        }

        this.classList.add('media-gallery--mobile-swiper');

        // Neutralise Dawn's scroll-snap slider on the same <ul>. Done here as
        // well as in the CSS file so it works even if that file is missing.
        list.style.setProperty('display', 'flex', 'important');
        list.style.setProperty('flex-wrap', 'nowrap', 'important');
        list.style.setProperty('overflow', 'visible', 'important');
        list.style.setProperty('scroll-snap-type', 'none', 'important');
        list.style.setProperty('gap', '0', 'important');
        list.style.setProperty('column-gap', '0', 'important');
        list.style.setProperty('margin', '0', 'important');
        list.style.setProperty('padding', '0', 'important');

        // Widths must be final before Swiper measures them.
        this.syncSlideWidths(container);

        // 4. Stop Dawn's SliderComponent from fighting us over the counter /
        //    arrow disabled-states on resize. Restored on destroy.
        if (typeof this.elements.viewer.update === 'function') {
          this.viewerUpdateOriginal = this.elements.viewer.update;
          this.elements.viewer.update = () => {};
        }

        const activeIndex = Math.max(
          0,
          Array.from(slides).findIndex((slide) => slide.classList.contains('is-active'))
        );

        this.mobileSwiper = new Swiper(container, {
          // 'auto' = build the snap grid from each slide's real measured
          // width, which syncSlideWidths() has just pinned down.
          slidesPerView: 'auto',
          spaceBetween: MOBILE_GALLERY_SWIPER.spaceBetween,
          autoHeight: MOBILE_GALLERY_SWIPER.autoHeight,
          loop: MOBILE_GALLERY_SWIPER.loop,
          initialSlide: activeIndex,
          threshold: 5, // ignore micro-drags so taps still open the zoom modal
          longSwipesRatio: 0.3, // a short flick still advances a slide
          observer: true, // re-measure when a variant change rewrites the <li>s
          observeParents: true,
          // let people drag a 3D model or scrub a video without swiping the gallery
          noSwipingSelector: 'product-model, model-viewer, video, iframe, .deferred-media__poster ~ *',
          a11y: { enabled: false }, // Dawn already has its own live region
          pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
          on: {
            slideChange: () => this.onSwiperSlideChange(),
            resize: () => {
              this.syncSlideWidths();
              this.mobileSwiper.update();
            },
          },
        });

        // 4b. Native HTML5 image drag steals the pointer and the swipe never
        //     gets a pointerup — the slide then parks between two positions.
        container.addEventListener('dragstart', (event) => event.preventDefault());

        // 5. Re-use Dawn's own arrows instead of adding new ones.
        //    Capture phase + stopPropagation neutralises SliderComponent's handler.
        this.elements.viewer.addEventListener('click', this.onNavButtonClick, true);
        this.updateSliderChrome();

        // 6. Variant changes rewrite the <li>s — keep Swiper in sync.
        this.swiperObserver = new MutationObserver(() => {
          clearTimeout(this.swiperRefreshTimer);
          this.swiperRefreshTimer = setTimeout(() => this.refreshMobileSwiper(), 50);
        });
        this.swiperObserver.observe(list, { childList: true });
      }

      destroyMobileSwiper() {
        if (this.swiperObserver) {
          this.swiperObserver.disconnect();
          this.swiperObserver = null;
        }
        clearTimeout(this.swiperRefreshTimer);

        if (this.elements.viewer) {
          this.elements.viewer.removeEventListener('click', this.onNavButtonClick, true);
          if (this.viewerUpdateOriginal) {
            this.elements.viewer.update = this.viewerUpdateOriginal;
            this.viewerUpdateOriginal = null;
          }
        }

        if (!this.mobileSwiper) return;

        const container = this.mobileSwiper.el;
        const list = this.mobileSwiper.wrapperEl;

        this.mobileSwiper.destroy(true, true);
        this.mobileSwiper = null;

        // Put the <ul> back exactly where Dawn expects it.
        if (list && container && container.parentNode) {
          list.classList.remove('swiper-wrapper');
          list.removeAttribute('style');
          list.querySelectorAll('.product__media-item').forEach((slide) => {
            slide.classList.remove('swiper-slide');
            slide.removeAttribute('style');
          });
          container.parentNode.insertBefore(list, container);
          container.remove();
        }

        this.classList.remove('media-gallery--mobile-swiper');

        // Hand the arrows / counter back to Dawn.
        if (this.elements.viewer && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
      }

      refreshMobileSwiper() {
        if (!this.mobileSwiper) {
          this.initMobileSwiper();
          return;
        }
        const list = this.mobileSwiper.wrapperEl;
        if (!list || !list.isConnected) {
          this.destroyMobileSwiper();
          this.initMobileSwiper();
          return;
        }
        list.querySelectorAll('.product__media-item').forEach((slide) => slide.classList.add('swiper-slide'));
        this.syncSlideWidths();
        this.mobileSwiper.update();
        this.updateSliderChrome();
      }

      onSwiperSlideChange() {
        const slide = this.mobileSwiper.slides[this.mobileSwiper.activeIndex];
        if (!slide) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        slide.classList.add('is-active');

        // Stop a video/model that's now off-screen.
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        if (MOBILE_GALLERY_SWIPER.playMediaOnSlideChange) this.playActiveMedia(slide);

        this.updateSliderChrome();

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      onNavButtonClick(event) {
        if (!this.mobileSwiper) return;
        const button = event.target.closest('.slider-buttons .slider-button');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        if (button.name === 'next') {
          this.mobileSwiper.slideNext();
        } else {
          this.mobileSwiper.slidePrev();
        }
      }

      // Keeps Dawn's "2 / 6" counter and arrow disabled states accurate.
      updateSliderChrome() {
        if (!this.mobileSwiper) return;
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (current) current.textContent = this.mobileSwiper.activeIndex + 1;
        if (total) total.textContent = this.mobileSwiper.slides.length;

        const prev = this.elements.viewer.querySelector('.slider-button--prev');
        const next = this.elements.viewer.querySelector('.slider-button--next');
        if (prev) prev.disabled = this.mobileSwiper.isBeginning;
        if (next) next.disabled = this.mobileSwiper.isEnd;
      }

      slideToMedia(activeMedia) {
        if (!this.mobileSwiper) return false;
        const index = Array.from(this.mobileSwiper.slides).indexOf(activeMedia);
        if (index === -1) return false;
        if (index !== this.mobileSwiper.activeIndex) this.mobileSwiper.slideTo(index);
        return true;
      }

      /* ================================================================
       * STOCK DAWN BELOW (only setActiveMedia is touched)
       * ================================================================ */

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          if (this.mobileSwiper) this.mobileSwiper.update();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // MOBILE: drive Swiper. DESKTOP: stock Dawn scroll.
          const handledBySwiper = this.slideToMedia(activeMedia);

          if (!handledBySwiper && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}